import { useMemo } from "react";
import type { LexicalEditor } from "lexical";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { EditorRefPlugin } from "@lexical/react/LexicalEditorRefPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";

import { getSuggestionOptionId } from "features/ui/SuggestionMenu/SuggestionMenu";

import { useDirectiveSlashSuggestions } from "./DirectiveComposer.hooks";
import type { DirectiveComposerState } from "./DirectiveComposer.types";
import { DirectiveInvocationNode } from "./DirectiveComposerInvocationNode";
import { DirectiveComposerPlugins } from "./DirectiveComposerPlugins";
import DirectiveComposerSuggestionMenu, {
  DIRECTIVE_COMPOSER_SUGGESTIONS_ID,
} from "./DirectiveComposerSuggestionMenu";

interface DirectiveComposerProps {
  disabled?: boolean;
  editorRef: React.RefObject<LexicalEditor | null>;
  inputRef: React.RefObject<HTMLElement | null>;
  placeholder: string;
  value: string;
  onBlur: () => void;
  onChange: (state: DirectiveComposerState) => void;
  onSubmit: () => void;
}

const DirectiveComposer = ({
  disabled = false,
  editorRef,
  inputRef,
  placeholder,
  value,
  onBlur,
  onChange,
  onSubmit,
}: DirectiveComposerProps) => {
  const {
    activeSuggestionIndex,
    handleArrowKey,
    matchingDirectives,
    setActiveSuggestionIndex,
    setSlashQuery,
  } = useDirectiveSlashSuggestions();
  const initialConfig = useMemo(
    () => ({
      namespace: "DuckDirectiveComposer",
      nodes: [DirectiveInvocationNode],
      onError(error: Error) {
        throw error;
      },
      theme: {},
    }),
    []
  );

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <PlainTextPlugin
        contentEditable={
          <ContentEditable
            aria-activedescendant={
              matchingDirectives.length > 0
                ? getSuggestionOptionId(
                    DIRECTIVE_COMPOSER_SUGGESTIONS_ID,
                    activeSuggestionIndex
                  )
                : undefined
            }
            aria-controls={DIRECTIVE_COMPOSER_SUGGESTIONS_ID}
            aria-expanded={matchingDirectives.length > 0}
            aria-haspopup="listbox"
            aria-label={placeholder}
            aria-multiline
            className="min-h-6 max-h-72 w-full overflow-y-auto whitespace-pre-wrap break-words px-1 text-base font-inherit outline-none empty:before:pointer-events-none empty:before:text-gray-400 empty:before:content-[attr(data-placeholder)]"
            data-placeholder={placeholder}
            data-testid="duck-text-input"
            role="textbox"
            tabIndex={0}
            onBlur={onBlur}
          />
        }
        ErrorBoundary={LexicalErrorBoundary}
      />
      <EditorRefPlugin editorRef={editorRef} />
      <DirectiveComposerPlugins
        activeSuggestionIndex={activeSuggestionIndex}
        disabled={disabled}
        inputRef={inputRef}
        matchingDirectives={matchingDirectives}
        value={value}
        onChange={onChange}
        onSlashQueryChange={setSlashQuery}
        onSubmit={onSubmit}
        onSuggestionArrowKey={handleArrowKey}
      />
      {matchingDirectives.length > 0 && (
        <DirectiveComposerSuggestionMenu
          activeIndex={activeSuggestionIndex}
          directives={matchingDirectives}
          onActiveIndexChange={setActiveSuggestionIndex}
        />
      )}
    </LexicalComposer>
  );
};

export default DirectiveComposer;
